/**
 * Sub-feature-local style primitives for the dataset-export surface.
 *
 * Resolves a tiny named palette off `sutra/tokens/colors` so the
 * Export button and modal stay visually consistent with the rest of
 * the app. No bespoke accent colors — every value below is a token
 * defined in sutra.
 */

import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined'
import styled, { keyframes } from 'styled-components'
import { accents, semantic } from 'sutra/tokens/colors'

const palette = {
  textPrimary: semantic.fg,
  textSecondary: semantic.fgMuted,
  textInverse: semantic.fgOnDark,
  bgMuted: semantic.bgSubtle,
  borderSubtle: semantic.border,
  primary: semantic.accent,
  primaryHover: semantic.accentDeep,
  success: accents.emeraldAura[500],
  error: accents.crimsonBloom[500],
} as const

export const MONO_STACK =
  "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

export const FinePrint = styled.span`
  font-size: 11px;
`

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${palette.textPrimary};
`

export const ModalSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${palette.textSecondary};
  text-transform: uppercase;
  font-weight: 600;
`

export const Section = styled.section`
  padding: 12px 0;
`

export const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${palette.textSecondary};
  margin-bottom: 8px;
`

export const SectionInfoIcon = styled(InfoCircleOutlined)`
  font-size: 12px;
  color: ${palette.textSecondary};
  margin-left: 4px;
`

export const ToggleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

export const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${palette.borderSubtle};
  margin: 0;
`

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`

export const FilenameDisplay = styled.code`
  font-family: ${MONO_STACK};
  font-size: 12px;
  color: ${palette.textPrimary};
  background: ${palette.bgMuted};
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
`

const indeterminateProgress = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

export const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background: ${palette.borderSubtle};
  overflow: hidden;
  border-radius: 1px;
`

export const ProgressBar = styled.div`
  position: absolute;
  inset: 0;
  background: ${palette.primary};
  animation: ${indeterminateProgress} 1.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`

export const ApplyButtonBase = styled.button<{
  $status: 'idle' | 'preparing' | 'success' | 'error'
}>`
  appearance: none;
  border: 1px solid ${palette.primary};
  background: ${({ $status }) =>
    $status === 'success' ? palette.success : palette.primary};
  color: ${palette.textInverse};
  font-weight: 600;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: ${({ $status }) =>
    $status === 'preparing' ? 'progress' : 'pointer'};
  transition:
    background 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
    border-color 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 120ms ease;
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:hover:not(:disabled) {
    background: ${({ $status }) =>
      $status === 'success' ? palette.success : palette.primaryHover};
    border-color: ${({ $status }) =>
      $status === 'success' ? palette.success : palette.primaryHover};
  }
  &:disabled {
    opacity: 0.6;
  }
`

const fadeUp = keyframes`
  from { opacity: 0; transform: scale(0.7); }
  to   { opacity: 1; transform: scale(1); }
`

export const SuccessBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* Same explicit pin as PreparingCopy — every footer status state reads
     left-aligned in the footer's left slot (Sogyel feedback, slide 9). */
  text-align: left;
  gap: 6px;
  animation: ${fadeUp} 240ms cubic-bezier(0.2, 0.8, 0.2, 1);
`

export const SuccessCheckmark = styled.span`
  font-size: 16px;
  color: ${palette.success};
  font-weight: 700;
`

export const ErrorText = styled.div`
  color: ${palette.error};
  font-size: 12px;
  letter-spacing: 0.02em;
`

export const PreparingCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* Pin explicitly — the antd modal footer sets text-align on its subtree,
     which otherwise re-aligns the stretched spans (Sogyel feedback, slide 9). */
  text-align: left;
  gap: 6px;
  font-size: 12px;
  color: ${palette.textSecondary};
`
